/**
 * MeetingsTab — lists `ahoy_meeting` entities associated with the
 * current record (typically a contact or company) via the
 * `ahoy_meeting_to_*` association. Sorted by start time descending so
 * upcoming and recent meetings appear first.
 *
 * Each row is a clickable card that opens the meeting record in the
 * slideout stack. Empty / loading / error states each render a single
 * compact card to match the visual rhythm of the highlight grid and
 * AssociationsTab.
 *
 * Today this only renders `ahoy_meeting` entities — call entities
 * don't exist yet. When a call entity type is added, run a second
 * parallel query here and merge the two streams into the same
 * date-sorted list so the "Calls / Meetings" framing in the tab is
 * accurate.
 */

import { useQuery } from '@tanstack/react-query';
import { Calendar } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useRefreshAt } from '@/hooks/useRefreshAt';
import { QueryErrorState } from '@/lib/components';
import { useNavigateToEntity } from '@/lib/crm/hooks/useNavigateToEntity';
import { formatTime, type TimeFormat, useOrgTimeFormat } from '@/lib/time';
import { getPropertyValue } from '@/types/crm';

import { createCRMObjectClient } from '../../core/CRMObjectClient';

interface MeetingsTabProps {
  orgId: number;
  entityId: string;
}

interface MeetingRow {
  entity_id: string;
  title: string;
  startTime: string | null;
}

const STALE_TIME = 60_000;

/**
 * Get the next timestamp when a meeting's "Upcoming" label should
 * change to a regular date label (i.e. the earliest future start time).
 */
function getNextMeetingTransition(
  meetings: ReadonlyArray<{ startTime: string | null }>
): number | null {
  const now = Date.now();
  let earliest: number | null = null;
  for (const meeting of meetings) {
    if (!meeting.startTime) {
      continue;
    }
    const startMs = new Date(meeting.startTime).getTime();
    if (startMs > now && (earliest === null || startMs < earliest)) {
      earliest = startMs;
    }
  }
  return earliest;
}

function formatMeetingDate(iso: string | null, timeFormat: TimeFormat): string {
  if (!iso) {
    return 'No date';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return iso;
  }
  const now = new Date();
  const isFuture = date.getTime() > now.getTime();
  const sameYear = date.getFullYear() === now.getFullYear();
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
  const timePart = formatTime(date, timeFormat, { locale: 'en-US' });
  return isFuture
    ? `Upcoming · ${datePart} · ${timePart}`
    : `${datePart} · ${timePart}`;
}

function ShellCard({
  children,
  label,
}: {
  children: React.ReactNode;
  label?: string;
}) {
  return (
    <div className='bg-primary border border-secondary rounded-xl px-3.5 py-3 shadow-xs'>
      {label && (
        <p className='text-xs font-medium text-[var(--color-text-secondary)] mb-1'>
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

export function MeetingsTab({ orgId, entityId }: MeetingsTabProps) {
  const navigateToEntity = useNavigateToEntity();
  const { timeFormat } = useOrgTimeFormat();
  const handleOpenMeeting = useCallback(
    (meetingEntityId: string) => {
      navigateToEntity(meetingEntityId, 'ahoy_meeting');
    },
    [navigateToEntity]
  );

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['meetings-tab', orgId, entityId],
    queryFn: async () => {
      const client = createCRMObjectClient(orgId, 'ahoy_meeting');
      return client.list({
        associated_with_entity_id: entityId,
        properties: ['ahoy_title', 'ahoy_start_time'],
        limit: 100,
      });
    },
    staleTime: STALE_TIME,
    enabled: !!orgId && !!entityId,
  });

  const meetings = useMemo<MeetingRow[]>(() => {
    const list = data?.results ?? [];
    return list
      .map(m => ({
        entity_id: m.entity_id,
        title:
          getPropertyValue<string>(m.properties?.ahoy_title) ||
          'Untitled meeting',
        startTime:
          getPropertyValue<string>(m.properties?.ahoy_start_time) ?? null,
      }))
      .sort((a, b) => {
        const at = a.startTime ?? '';
        const bt = b.startTime ?? '';
        // Sort by start_time descending; rows without a start_time
        // sink to the bottom rather than scrambling the order.
        if (!at && !bt) {
          return 0;
        }
        if (!at) {
          return 1;
        }
        if (!bt) {
          return -1;
        }
        return bt.localeCompare(at);
      });
  }, [data]);

  // Schedule re-render when the next "Upcoming" meeting starts,
  // so its label updates from "Upcoming · ..." to just the date.
  useRefreshAt(getNextMeetingTransition(meetings));

  if (isLoading) {
    return (
      <div className='space-y-2' aria-busy='true' aria-label='Loading meetings'>
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className='bg-primary border border-secondary rounded-xl px-3.5 py-3 shadow-xs h-14 animate-pulse'
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <ShellCard label='Meetings'>
        <QueryErrorState
          error={error}
          label="Couldn't load meetings. Try again later."
          size='sm'
        />
      </ShellCard>
    );
  }

  if (meetings.length === 0) {
    return (
      <ShellCard label='Meetings'>
        <p className='text-md font-medium text-[var(--color-text-tertiary)]'>
          No meetings yet
        </p>
      </ShellCard>
    );
  }

  return (
    <div className='space-y-2'>
      {meetings.map(meeting => (
        <button
          key={meeting.entity_id}
          type='button'
          onClick={() => handleOpenMeeting(meeting.entity_id)}
          aria-label={`Open meeting: ${meeting.title}`}
          className='w-full text-left bg-primary border border-secondary rounded-xl px-3.5 py-3 shadow-xs hover:bg-[var(--color-bg-secondary)] hover:border-primary transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring'
        >
          <div className='flex items-center gap-3 min-w-0'>
            <Calendar
              className='w-4 h-4 shrink-0 text-fg-quaternary'
              aria-hidden='true'
            />
            <div className='min-w-0 flex-1'>
              <p className='text-sm font-semibold text-[var(--color-text-primary)] truncate'>
                {meeting.title}
              </p>
              <p className='text-xs text-[var(--color-text-tertiary)] truncate'>
                {formatMeetingDate(meeting.startTime, timeFormat)}
              </p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}
